import { z } from 'zod';
import { ORDER_STOP_TYPES } from '../constants';
import { SchemaSubmitOrderDto, SchemaSubmitOrderLimitDtoData } from '../generated/apiTypes';
import { Gwei } from '../lib/gwei';
import { PrettyMerge } from '../lib/tsUtilityTypes';
import { OrderSide } from './generated';

// Instead of replicated the full (fairly complicated) order type,
// we just do a simple validation with fields we use.
export const OrderUpdateSchema = z.object({
  id: z.string().uuid(),
  status: z.enum([
    'CANCELED',
    'EXPIRED',
    'FILLED',
    'FILLED_PARTIAL',
    'NEW',
    'PENDING',
    'REJECTED',
    'SUBMITTED',
  ]),
  productId: z.string().uuid(),
  subaccountId: z.string().uuid(),
  close: z.boolean(),
  stopType: z.number().nullable().optional(),
  type: z.enum(['LIMIT', 'MARKET']),
  quantity: z.string().transform(Gwei.toGwei),
  availableQuantity: z.string().transform(Gwei.toGwei),
  price: z.string().transform(Gwei.toGwei).nullable().optional(),
  stopPrice: z.string().transform(Gwei.toGwei).nullable().optional(),
});
export type WsOrder = z.infer<typeof OrderUpdateSchema>;

export type OrderStopType = (typeof ORDER_STOP_TYPES)[keyof typeof ORDER_STOP_TYPES];
export type OrderType = SchemaSubmitOrderDto['data']['type'];
export type TimeInForce = SchemaSubmitOrderLimitDtoData['timeInForce'];

export type RawOrderFormData = {
  amount: bigint;
  side: OrderSide;
  reduceOnly?: boolean;
  close: boolean | undefined;
  price?: bigint;
  timeInForce?: TimeInForce;
  postOnly?: boolean;
  stopType?: OrderStopType;
  stopPrice?: bigint;
  type: OrderType;
  // OTOCO order
  otocoGroupId?: string;
  // OTO order
  otocoTrigger?: boolean;
  stopLoss?: bigint;
  takeProfit?: bigint;

  /** Seconds from now. The order's `expiresAt` will be calculated based on it. */
  expiresFromNow?: number;
};

export type SimpleLimitOrder = {
  discriminator: 'SimpleLimitOrder';
  amount: bigint;
  side: OrderSide;
  reduceOnly: boolean;
  type: 'LIMIT';
  price: bigint;
  timeInForce: TimeInForce;
  postOnly: boolean;
  expiresAt?: number;
};
export type SimpleMarketOrder = {
  discriminator: 'SimpleMarketOrder';
  amount: bigint;
  side: OrderSide;
  reduceOnly: boolean;
  type: 'MARKET';
};
export type CloseMarketOrder = {
  discriminator: 'CloseMarketOrder';
  type: 'MARKET';
  close: true;
  reduceOnly: true;
  side: OrderSide;
  amount: 0n;
};

export type OtoLimitOrder = PrettyMerge<
  SimpleLimitOrder,
  {
    discriminator: 'OtoLimitOrder';
    otocoTrigger: true;
    stopLoss?: bigint;
    takeProfit?: bigint;
  }
>;

export type PositionStopOrder = {
  discriminator: 'PositionStopOrder';
  type: 'MARKET';
  amount: bigint;
  side: OrderSide;
  stopPrice: bigint;
  stopType: OrderStopType;
  close: true;
  reduceOnly: true;
};

export type OtocoStopOrder = {
  discriminator: 'OtocoStopOrder';
  type: 'MARKET';
  amount: bigint;
  side: OrderSide;
  stopPrice: bigint;
  stopType: OrderStopType;
  close: false;
  reduceOnly: true;
  otocoGroupId: string;
};

export type ValidatedOrder =
  | SimpleLimitOrder
  | SimpleMarketOrder
  | PositionStopOrder
  | CloseMarketOrder
  | OtocoStopOrder
  | OtoLimitOrder;
